import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, RefreshCw, Droplets, Ban, AlertTriangle, CheckCircle2, Flame } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SOURCE_THEME = {
  'auto-fire':       { icon: Flame,          color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/40',   label: 'AUTO-FIRE' },
  'manual':          { icon: Droplets,       color: 'text-sky-400',     bg: 'bg-sky-500/10',     border: 'border-sky-500/40',     label: 'MANUAL' },
  'apply-all':       { icon: CheckCircle2,   color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', label: 'APPLY-ALL' },
  'rachio-dispatch': { icon: Droplets,       color: 'text-sky-400',     bg: 'bg-sky-500/10',     border: 'border-sky-500/40',     label: 'RACHIO FIRE' },
  'rule-trigger':    { icon: AlertTriangle,  color: 'text-rose-400',    bg: 'bg-rose-500/10',    border: 'border-rose-500/40',    label: 'RULE TRIGGER' },
  'suppression':     { icon: Ban,            color: 'text-indigo-400',  bg: 'bg-indigo-500/10',  border: 'border-indigo-500/40',  label: 'SUPPRESSED' },
  'other':           { icon: AlertTriangle,  color: 'text-slate-400',   bg: 'bg-slate-500/10',   border: 'border-slate-500/40',   label: 'OTHER' },
};

function fmtTs(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

function EventRow({ ev }) {
  const theme = SOURCE_THEME[ev.source] || SOURCE_THEME.other;
  const Icon = theme.icon;
  return (
    <div
      data-testid={`fire-event-${ev.kind}`}
      className={`rounded-lg border ${theme.border} ${theme.bg} p-3 mb-2`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${theme.bg}`}>
          <Icon className={`w-5 h-5 ${theme.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-wider font-bold ${theme.color}`}>
              {theme.label}
            </span>
            <span className="text-[10px] text-slate-500">{fmtTs(ev.ts)}</span>
          </div>
          <div className="text-sm font-semibold text-slate-100 leading-snug mt-0.5">
            {ev.rule_summary || ev.kind}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400 flex-wrap">
            {ev.zone_label && (
              <span>Zone: <b className="text-slate-200">{ev.zone_label}</b></span>
            )}
            {ev.moisture_pct != null && (
              <span>Moisture: <b className="text-slate-200">{ev.moisture_pct}%</b></span>
            )}
            {ev.runtime_min != null && (
              <span>Runtime: <b className="text-slate-200">{ev.runtime_min} min</b></span>
            )}
            {ev.severity && (
              <span className="text-amber-400 font-bold">{ev.severity}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RecentFiresPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState('fires');

  const fetchFires = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/debug/recent-fires?days=${days}`, {
        credentials: 'include',
      });
      if (res.status === 401) {
        throw new Error('Your session expired — sign in again.');
      }
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}${body ? ` — ${body.slice(0, 140)}` : ''}`);
      }
      setData(await res.json());
    } catch (e) {
      // Safari surfaces network-level failures as "Load failed" — show
      // something actionable instead of just the raw message.
      const msg = e?.message || String(e);
      setError(msg === 'Load failed'
        ? 'Network error — pull down to retry, or sign in again.'
        : msg);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchFires(); }, [fetchFires]);

  const tabs = data ? {
    fires: data.fires || [],
    rule_triggers: data.rule_triggers || [],
    suppressions: data.suppressions || [],
    events: data.events || [],
  } : { fires: [], rule_triggers: [], suppressions: [], events: [] };

  const currentList = tabs[tab] || [];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pb-20">
      <div className="max-w-md mx-auto px-4 pt-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-slate-400 hover:text-slate-100"
            data-testid="fires-back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          <button
            onClick={fetchFires}
            className="p-2 rounded-full hover:bg-slate-800 transition-colors"
            data-testid="fires-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <h1 className="text-2xl font-extrabold tracking-tight">Recent Fires</h1>
        <p className="text-sm text-slate-400 mt-0.5">
          Every Rachio dispatch + every rule that fired or got suppressed.
        </p>

        {/* Day-range pills */}
        <div className="flex gap-2 mt-3">
          {[1, 7, 30].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border transition-colors ${
                days === d
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                  : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200'
              }`}
              data-testid={`fires-range-${d}`}
            >
              {d === 1 ? '24h' : `${d}d`}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-4 flex gap-1 border-b border-slate-800">
          {[
            { id: 'fires',         label: `Fires (${tabs.fires.length})` },
            { id: 'suppressions',  label: `Suppressed (${tabs.suppressions.length})` },
            { id: 'rule_triggers', label: `Triggers (${tabs.rule_triggers.length})` },
            { id: 'events',        label: `All (${tabs.events.length})` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-xs font-semibold border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-emerald-400 text-emerald-300'
                  : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
              data-testid={`fires-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="mt-4">
          {loading && (
            <div className="text-center text-slate-400 py-8 text-sm">Loading…</div>
          )}
          {error && (
            <div className="text-rose-400 text-sm py-8 text-center" data-testid="fires-error">
              Error: {error}
            </div>
          )}
          {!loading && !error && currentList.length === 0 && (
            <div className="text-center text-slate-500 py-12 text-sm" data-testid="fires-empty">
              Nothing in the last {days === 1 ? '24 hours' : `${days} days`}.
              <br />
              {tab === 'fires' && 'No Rachio dispatches yet.'}
              {tab === 'suppressions' && 'No rules blocked a fire — engine clear.'}
              {tab === 'rule_triggers' && 'No probes breached an Action/Heads-up threshold.'}
            </div>
          )}
          {!loading && !error && currentList.map((ev, i) => (
            <EventRow key={`${ev.ts}-${i}`} ev={ev} />
          ))}
        </div>
      </div>
    </div>
  );
}
